import { Box, CardMedia, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";

import { getDefaultCountryFlag } from "../data/defaultCountryFlags";
import { useCountry } from "../data/useCountry";

interface FlagProps {
  country?: string;
  style?: CSSProperties;
}

export function Flag({ country, style }: FlagProps): JSX.Element | null {
  const { flag, name, assignedTo, assigned } = useCountry(country);
  const defaultFlag = useMemo(() => getDefaultCountryFlag(country), [country]);
  const [useDefaultFlag, setUseDefaultFlag] = useState(false);
  const fallbackFlag = defaultFlag?.flag;
  const displayName = name || defaultFlag?.name || country;
  const flagSrc = useDefaultFlag ? fallbackFlag : flag || fallbackFlag;

  const assignedParticipant = assignedTo || assigned || "";
  const title = `${displayName ?? ""}: ${assignedParticipant}`;

  useEffect(() => {
    setUseDefaultFlag(false);
  }, [country, flag, fallbackFlag]);

  if (!flagSrc) {
    if (!country) return null;

    return (
      <Box
        title={title}
        sx={{
          alignItems: "center",
          bgcolor: "grey.100",
          borderColor: "divider",
          borderStyle: "solid",
          borderWidth: 0,
          display: "flex",
          height: 100,
          justifyContent: "center",
          overflow: "hidden",
          position: "absolute",
          width: "50%",
        }}
        style={style}
      >
        <Typography
          component="span"
          sx={{
            color: "text.secondary",
            fontSize: 14,
            fontWeight: 600,
            px: 1.5,
            textAlign: "center",
            wordBreak: "break-word",
          }}
        >
          {country}
        </Typography>
      </Box>
    );
  }

  return (
    <CardMedia
      component="img"
      src={flagSrc}
      title={title}
      alt={title}
      onError={() => {
        if (fallbackFlag && flagSrc !== fallbackFlag) {
          setUseDefaultFlag(true);
        }
      }}
      sx={{
        height: 100,
        objectFit: "cover",
        position: "absolute",
        width: "50%",
      }}
      style={style}
    />
  );
}
